import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Article,
  GroupSettings,
  RestrictedGroup,
  RestrictedWord,
  RestrictedWordsState,
  SetInitDataPayload,
  UpdateWordPolicyPayload,
  WordSettings
} from './restrictedWordsTypes';
import { getGlobalGroup, isGlobalGroup } from './restrictedWordsUtils';

const initialState: RestrictedWordsState = {
  words: [],
  searchWords: [],
  groups: [],
  isLoading: false,
  selectedGroup: getGlobalGroup(),
  globalWordsCount: 0,
  wordSettings: null,
  groupSettings: null,
  articles: []
};

const reducers = {
  setRestrictedWordsIsLoading(state: RestrictedWordsState, action: PayloadAction<boolean>) {
    state.isLoading = action.payload;
  },
  setRestrictedWords(state: RestrictedWordsState, action: PayloadAction<Array<RestrictedWord>>) {
    state.words = action.payload;

    maybeUpdateGlobalGroupCount(state);
  },
  setSearchWords(state: RestrictedWordsState, action: PayloadAction<Array<RestrictedWord>>) {
    state.searchWords = action.payload;
  },
  setRestrictedGroups(state: RestrictedWordsState, action: PayloadAction<Array<RestrictedGroup>>) {
    state.groups = action.payload;

    syncSelectedGroup(state);
  },
  setInitData(state: RestrictedWordsState, action: PayloadAction<SetInitDataPayload>) {
    const { words, searchWords, groups } = action.payload;

    state.words = words;
    state.searchWords = searchWords;
    state.groups = groups;

    syncSelectedGroup(state);

    maybeUpdateGlobalGroupCount(state);
  },
  changeSelectedGroup(state: RestrictedWordsState, action: PayloadAction<RestrictedGroup>) {
    state.selectedGroup = action.payload;
  },
  updateWordPolicy(state: RestrictedWordsState, action: PayloadAction<UpdateWordPolicyPayload>) {
    const {
      word: { id, policy }
    } = action.payload;
    const wordToUpdate = state.words.find(w => w.id === id);

    if (wordToUpdate) {
      wordToUpdate.policy = policy;
    }

    const searchWordToUpdate = state.searchWords.find(w => w.id === id);

    if (searchWordToUpdate) {
      searchWordToUpdate.policy = policy;
    }
  },
  addRestrictedWords(state: RestrictedWordsState, action: PayloadAction<Array<RestrictedWord>>) {
    state.words = [...action.payload, ...state.words];

    maybeUpdateGlobalGroupCount(state);
  },
  removeRestrictedWords(state: RestrictedWordsState, action: PayloadAction<Array<RestrictedWord>>) {
    const wordsToRemove = action.payload;

    state.words = state.words.filter(
      word => !wordsToRemove.some(wordToRemove => wordToRemove.id === word.id)
    );

    maybeUpdateGlobalGroupCount(state);
  },
  openWordSettings(state: RestrictedWordsState, action: PayloadAction<WordSettings>) {
    const restrictedWord = action.payload;
    state.wordSettings = {
      ...restrictedWord
    };
  },
  closeWordSettings(state: RestrictedWordsState) {
    state.wordSettings = null;
  },
  openGroupSettings(state: RestrictedWordsState, action: PayloadAction<GroupSettings>) {
    const group = action.payload;
    state.groupSettings = {
      ...group
    };
  },
  closeGroupSettings(state: RestrictedWordsState) {
    state.groupSettings = null;
  },
  setWordSettings(state: RestrictedWordsState, action: PayloadAction<WordSettings>) {
    const restrictedWordData = action.payload;
    restrictedWordData.exceptions = action.payload?.exceptions || [];
    state.wordSettings = {
      ...state.wordSettings,
      ...restrictedWordData
    };
  },
  setArticles(state: RestrictedWordsState, action: PayloadAction<Array<Article>>) {
    const articles = action.payload;
    state.articles = articles;
  },
  setRestrictionGroups(state: RestrictedWordsState, action: PayloadAction<Array<RestrictedGroup>>) {
    const restrictionGroupsData = action.payload;
    state.wordSettings.restrictionGroups = restrictionGroupsData;
  },
  resetArticles(state: RestrictedWordsState) {
    state.articles = [];
  }
};

const restrictedWordsSlice = createSlice({
  name: 'restrictedWords',
  initialState,
  reducers
});

export const restrictedWordsReducer = restrictedWordsSlice.reducer;

export const restrictedWordsActions = restrictedWordsSlice.actions;
export const {
  setRestrictedWordsIsLoading,
  setRestrictedWords,
  updateWordPolicy,
  setSearchWords,
  setRestrictedGroups,
  setInitData,
  changeSelectedGroup,
  addRestrictedWords,
  removeRestrictedWords,
  openWordSettings,
  openGroupSettings,
  closeGroupSettings,
  closeWordSettings,
  setWordSettings,
  setRestrictionGroups,
  setArticles,
  resetArticles
} = restrictedWordsActions;

function maybeUpdateGlobalGroupCount(state: RestrictedWordsState) {
  if (isGlobalGroup(state.selectedGroup)) {
    state.selectedGroup.wordsCount = state.globalWordsCount = state.words.length;
  }
}

function syncSelectedGroup(state: RestrictedWordsState) {
  const selectedGroup = state.selectedGroup;
  const selectedGroupNew = state.groups.find(group => group.id === selectedGroup.id);

  if (
    selectedGroupNew &&
    (selectedGroupNew.wordsCount !== selectedGroup.wordsCount ||
      selectedGroupNew.value !== selectedGroup.value)
  ) {
    state.selectedGroup = selectedGroupNew;
  }
}
